import maplibregl from "maplibre-gl";
import { customMarker } from "@/helpers/StyleHelpers";
import Helpers from "@/helpers/Helpers";

export default class PointMode {
  static on(eventName, callback) {
    if (!this.callbacks[eventName]) {
      console.debug("Invalid event name", eventName);
      return;
    }
    if (typeof callback !== "function") {
      console.debug("Invalid callback", callback);
      return;
    }
    this.callbacks[eventName].push(callback);
  }

  static off(eventName, callback) {
    this.callbacks[eventName].splice(this.callbacks[eventName].indexOf(callback), 1);
  }

  static init() {
    this.isEnabled = false;
    this.markers = {};
    this.callbacks = {};
    this.markersFromClick = {};
    this.lastCreatedMarkerId = null; // Add this to track the last created marker
    this.events = ["point.create", "point.update", "point.delete"];

    this.events.forEach((eventName) => (this.callbacks[eventName] = []));
  }

  static create({ map }) {
    this.map = map;
    this.mapClick = this.mapClick.bind(this);
  }

  // Not used anywhere
  static destroy() {
    if (!this.map) {
      return;
    }
    this.callbacks["point.delete"].forEach((callback) => callback());
    this.clearPoints();
    this.init();
    this.toggle({ shouldEnable: false });
  }

  static mapClick(e) {
    const { lng, lat } = e.lngLat;
    const markerId = Helpers.generateUuid();
    const markerEl = customMarker({ markerId: markerId, className: "node-marker-point" });
    this.addMarker(markerEl, [lng, lat], markerId, { draggable: true, isFromMapClick: true });
  }

  static addPoint(coordinate, options) {
    const markerId = options.fid ? options.fid : Helpers.generateUuid();
    // Checks if point/marker is already added before, if added skips
    if (this.markers[markerId]) {
      return;
    }
    const markerEl = customMarker({
      markerId: markerId,
      className: options?.isBeacon ? "beacon-marker" : "node-marker-point"
    });
    this.addMarker(markerEl, coordinate, markerId, options);
    return markerEl;
  }

  static movePoint(markerId, coordinate) {
    this.markers?.[markerId].setLngLat(coordinate);
  }

  // enable/disable
  static toggle(options) {
    if (!this.map) {
      return; // setup not called yet. So we don't need toggle
    }
    this.isEnabled = options?.shouldEnable ?? !this.isEnabled;

    if (this.isEnabled && options?.shouldAddOnce) {
      this.map.once("click", this.mapClick);
    } else if (this.isEnabled) {
      this.map.on("click", this.mapClick);
    } else {
      this.map.off("click", this.mapClick);
      document.getElementsByClassName("map-wrapper")[0].classList.remove("crosshair");
    }
    // Object.values(this.markers).forEach((marker) => {
    //   marker.setDraggable(this.isEnabled);
    //   marker.on("drag", () => {
    //     this.callbacks["point.update"].forEach((callback) => callback());
    //   });
    // });
  }

  static makeMarkerDraggable(markerId) {
    this.markers?.[markerId]?.setDraggable(true);
    this.markers?.[markerId]?.on("drag", () => {
      this.callbacks["point.update"].forEach((callback) =>
        callback([this.markers?.[markerId]?.getLngLat().lng, this.markers?.[markerId]?.getLngLat().lat])
      );
    });
  }

  static makeMarkerNonDraggable(markerId) {
    this.markers?.[markerId]?.setDraggable(false);
    this.markers?.[markerId]?.off("drag");
  }

  static addMarker(markerEl, coord, markerId, options) {
    if (!coord) {
      return;
    }
    let marker = new maplibregl.Marker({ element: markerEl, draggable: options.draggable });
    marker.setLngLat(coord).addTo(this.map);
    if (options.draggable) {
      marker.on("drag", () => {
        this.callbacks["point.update"].forEach((callback) =>
          callback([marker.getLngLat().lng, marker.getLngLat().lat])
        );
      });
    }
    if (options.isFromMapClick) {
      this.markersFromClick[markerId] = marker;
      this.lastCreatedMarkerId = markerId; // Track the last created marker ID
    }
    this.markers[markerId] = marker;

    this.callbacks["point.create"].forEach((callback) => callback([marker.getLngLat().lng, marker.getLngLat().lat]));
  }

  static highlight(markerId) {
    if (this.markers[markerId]) {
      this.markers[markerId].getElement().classList.add("node-marker-selected");
    }
  }

  static unhighlight(markerId) {
    if (this.markers[markerId]) {
      this.markers[markerId].getElement().classList.remove("node-marker-selected");
    }
  }

  static removeMarker(markerIdToRemove) {
    Object.keys(this.markers).some((markerId) => {
      if (markerId == markerIdToRemove) {
        this.markers[markerIdToRemove].getElement().remove();
        delete this.markers[markerIdToRemove];
        return true;
      }
    });
  }

  static removeMarkerFromClicks() {
    Object.keys(this.markersFromClick).forEach((markerId) => {
      this.markersFromClick[markerId].getElement().remove();
      delete this.markersFromClick[markerId];
      if (this.lastCreatedMarkerId == markerId) {
        this.lastCreatedMarkerId = null;
      }
    });
  }

  static clearPoints() {
    Object.keys(this.markers).forEach((markerId) => {
      this.markers[markerId].getElement().remove();
    });
    this.markers = {};
  }

  static exportCoordinates() {
    const lngLatObjArr = Object.values(this.markers).map((obj) => obj.getLngLat());
    return lngLatObjArr.reduce((total, lngLat) => {
      const { lng, lat } = lngLat;
      total.push([lng, lat]);
      return total;
    }, []);
  }

  static getLastCreatedMarkerId() {
    return this.lastCreatedMarkerId;
  }

  static hasMarker(markerId) {
    return this.markers && this.markers[markerId] !== undefined;
  }
}
